// components/TableComponent.tsx
import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
} from '@mui/material';

export interface Column {
    key: string;
    label: string;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    align?: 'left' | 'center' | 'right';
    render?: (value: any, record: any) => React.ReactNode;
}

interface TableComponentProps {
    data: any[];
    columns: Column[];
    loading?: boolean;
    emptyMessage?: string;
}

const textOverflowStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const TableComponent: React.FC<TableComponentProps> = ({
    data,
    columns,
    loading = false,
    emptyMessage = 'No hay datos disponibles',
}) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (data.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            overflowX: 'auto',
            pb: 1,
        }}>
            <TableContainer
                component={Paper}
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                    '& .MuiTableCell-root': {
                        padding: '6px 8px',
                    }
                }}
            >
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    sx={{
                                        fontWeight: 'bold',
                                        width: column.width,
                                        minWidth: column.minWidth,
                                        maxWidth: column.maxWidth,
                                        textAlign: column.align || 'left',
                                        ...textOverflowStyle
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((record, index) => (
                            <TableRow key={record.id || index} hover>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        sx={{
                                            width: column.width,
                                            minWidth: column.minWidth,
                                            maxWidth: column.maxWidth,
                                            textAlign: column.align || 'left',
                                            ...textOverflowStyle
                                        }}
                                    >
                                        {column.render
                                            ? column.render(record[column.key], record)
                                            : record[column.key]
                                        }
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TableComponent;